import { RestError } from '@azure/core-http';
import { UserDto, KnownUserDtoUserType, KnownProjectDtoProjectType } from '@groundfloor-platform/mgmt-service-g-sdk';
import * as _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { useQueryGetProject, useQueryGetProjectChildProjects } from '../../../../../core/queries/projects.queries';
import {
  useMutationCreateCustomer,
  useMutationCreateOccupant,
  useMutationEditOccupant,
  useMutationEditCustomer,
  useMutationEditUser
} from '../../../users.queries';
import { EditUserActionProps } from './edit-user-action.component';
import { useQueryListGroups } from '../../../../groups/groups.queries';
import { mobilePhoneValidator } from '../../../../../ui/util/mobile-phone.validator';
import { getProjectMobileCountry, hasMobilePrefix } from '../../../../../ui/util/project-mobile-country';
import { mapProjectTypeToUserType } from '../../../users.constants';
import { useGroupLabels, useProjectLabels } from '../../../../../hooks/use-entity-labels.hook';
import {
  mapUserDtoToUserForm,
  mapUserFormToCreateCustomerDto,
  mapUserFormToCreateOccupantDto,
  mapUserFormToEditCustomerDto,
  mapUserFormToEditOccupantDto,
  mapUserFormToEditUserDto
} from '../../logic/user-details-props.map';
import { useForm } from 'react-hook-form';
import { useImmer } from 'use-immer';
import { castDraft } from 'immer';
import { FormConfigBase, FormConfigItem } from '../../../../../core/forms/form-config';
import { naturalSort } from '../../../../../core/util/natural-sort.util';

export type CreateOrEditUserFormState = {
  firstName: string;
  lastName?: string;
  preferredName?: string;
  maidenName?: string;
  projectID: string;
  groupName: string;
  email?: string;
  mobile?: string;
  externalID?: string;
  prefersLowerLockers?: boolean;
  prefersAudioPrompts?: boolean;
};

export type EditUserActionState = {
  saving: boolean;
  loading: boolean;
  error?: RestError;
};

export interface EditUserActionHandlers {
  onCancel(): void;
  onSubmit(form: CreateOrEditUserFormState): void;
}

export function useEditUserAction({ userDto, projectID, childProjectID, onAbort, onSuccess }: EditUserActionProps) {
  const [state, setState] = useImmer<EditUserActionState>({ loading: true, saving: false });

  const form = useCreateOrEditUserForm(userDto, childProjectID ?? projectID);

  const defaultProject = useQueryGetProject(childProjectID ?? projectID);
  const currentProject = useQueryGetProject(form.watch('projectID'));
  const projectType = currentProject.data?.projectType ?? defaultProject.data?.projectType;
  const currentUserType = mapProjectTypeToUserType(projectType);

  const projects = useQueryGetProjectChildProjects(childProjectID || projectID, {});
  const formProjectID = form.watch('projectID');

  // Exclude top level groups if the project type is not Retail and not Store.
  const excludeTopLevelGroups =
    projectType !== KnownProjectDtoProjectType.Retailer && projectType !== KnownProjectDtoProjectType.Store;
  const groups = useQueryListGroups(formProjectID, excludeTopLevelGroups);

  useEffect(() => {
    if (projects.isLoading || groups.isLoading) {
      setState((draft) => {
        draft.loading = true;
      });
    } else {
      setState((draft) => {
        draft.loading = false;
      });
    }
  }, [groups.isLoading, projects.isLoading, setState]);

  // If the projectID changes, clear the group name (Only when creating a new user)
  useEffect(() => {
    if (!userDto && formProjectID) {
      form.setValue('groupName', '');
    }
  }, [userDto, formProjectID]);

  const projectOptions = useMemo(
    () =>
      (projects.data || [])
        .filter((project) =>
          [KnownUserDtoUserType.Customer, KnownUserDtoUserType.Occupant].includes(
            mapProjectTypeToUserType(project.projectType)
          )
        )
        .map((project) => ({
          label: project.name,
          value: project.id
        })),
    [projects.data]
  );

  // If there is only 1 project then select it
  useEffect(() => {
    if (projectOptions.length === 1) {
      form.setValue('projectID', projectOptions[0].value);
    }
  }, [projectOptions]);

  const groupOptions = useMemo(() => {
    const groupNames = groups.data?.map((group) => group.name) || [];
    return naturalSort(groupNames).asc();
  }, [groups.data]);

  const editUserMutation = useMutationEditUser(userDto?.id);
  const editCustomerMutation = useMutationEditCustomer(userDto?.id);
  const editOccupantMutation = useMutationEditOccupant(userDto?.id);
  const createCustomerMutation = useMutationCreateCustomer();
  const createOccupantMutation = useMutationCreateOccupant();

  const handlers: EditUserActionHandlers = {
    onCancel: onAbort,
    onSubmit(form) {
      setState((draft) => {
        draft.saving = true;
      });
      if (!userDto) {
        switch (currentUserType) {
          case KnownUserDtoUserType.Customer:
            const createCustomerDto = mapUserFormToCreateCustomerDto(form);
            createCustomerMutation.mutate(
              { retailerID: form.projectID, createCustomerDto },
              {
                onSuccess() {
                  onSuccess();
                },
                onError(error) {
                  setState((draft) => {
                    draft.saving = false;
                    draft.error = castDraft(error);
                  });
                }
              }
            );
            break;
          case KnownUserDtoUserType.Occupant:
            const createOccupant = mapUserFormToCreateOccupantDto(form);
            createOccupantMutation.mutate(createOccupant, {
              onSuccess() {
                onSuccess();
              },
              onError(error) {
                setState((draft) => {
                  draft.saving = false;
                  draft.error = castDraft(error);
                });
              }
            });
            break;
          default:
            // Do Nothing
            break;
        }
      } else {
        switch (currentUserType) {
          case KnownUserDtoUserType.Customer:
            const editCustomerDto = mapUserFormToEditCustomerDto(form, userDto);
            editCustomerMutation.mutate(
              { retailerID: form.projectID, editCustomerDto },
              {
                onSuccess() {
                  onSuccess();
                },
                onError(error) {
                  setState((draft) => {
                    draft.saving = false;
                    draft.error = castDraft(error);
                  });
                }
              }
            );
            break;
          case KnownUserDtoUserType.Occupant:
            const editOccupant = mapUserFormToEditOccupantDto(form, userDto);
            editOccupantMutation.mutate(editOccupant, {
              onSuccess() {
                onSuccess();
              },
              onError(error) {
                setState((draft) => {
                  draft.saving = false;
                  draft.error = castDraft(error);
                });
              }
            });
            break;
          default:
            const editUserDto = mapUserFormToEditUserDto(form, userDto);
            editUserMutation.mutate(editUserDto, {
              onSuccess() {
                onSuccess();
              },
              onError(error) {
                setState((draft) => {
                  draft.saving = false;
                  draft.error = castDraft(error);
                });
              }
            });

            break;
        }
      }
    }
  };

  return { state, handlers, form, projectOptions, groupOptions, currentUserType } as const;
}

export function useCreateOrEditUserForm(userDto: UserDto | undefined, projectID: string) {
  const { watch, control, handleSubmit, setValue } = useForm<CreateOrEditUserFormState>({
    ...(userDto && {
      defaultValues: mapUserDtoToUserForm(projectID, userDto)
    })
  });

  const defaultProject = useQueryGetProject(projectID);
  const defaultProjectType = defaultProject.data?.projectType;
  const currentProjectID = watch('projectID');
  const currentSelectedProject = useQueryGetProject(currentProjectID);
  const currentProjectType = currentSelectedProject.data?.projectType;
  const currentUserType = mapProjectTypeToUserType(currentProjectType ?? defaultProjectType);
  const mobileCountry = getProjectMobileCountry((currentSelectedProject.data ?? defaultProject.data)?.address?.country);
  const currentGroupLabel = useGroupLabels(currentProjectID ?? projectID);
  const currentProjectLabel = useProjectLabels(currentProjectID ?? projectID);

  const formConfigBase: FormConfigBase<Omit<CreateOrEditUserFormState, 'maidenName'>> = {
    firstName: {
      name: 'firstName',
      label: 'First name',
      required: true,
      validate: (value) => !_.isEmpty(value)
    },
    lastName: {
      name: 'lastName',
      label: 'Last name',
      required: false
    },
    preferredName: {
      name: 'preferredName',
      label: 'Preferred name',
      required: false
    },
    email: {
      name: 'email',
      label: 'Email address',
      required: _.isEmpty(watch('mobile'))
    },
    mobile: {
      name: 'mobile',
      label: 'Mobile number',
      required: _.isEmpty(watch('email')),
      validate: (mobile: string | undefined) => {
        if (!mobile) {
          return true;
        }
        if (!hasMobilePrefix(mobile, mobileCountry)) {
          return `Mobile number must start with ${mobileCountry.prefix} (${mobileCountry.name})`;
        }
        return mobilePhoneValidator(mobile);
      }
    },
    externalID: {
      name: 'externalID',
      label: 'External ID',
      required: false
    },
    prefersLowerLockers: {
      name: 'prefersLowerLockers',
      label: 'Prefers lower lockers',
      required: false
    },
    prefersAudioPrompts: {
      name: 'prefersAudioPrompts',
      label: 'Prefers audio guidance',
      required: false
    },
    groupName: {
      name: 'groupName',
      label: currentGroupLabel.singular,
      required: currentUserType !== KnownUserDtoUserType.Customer
    },
    projectID: {
      name: 'projectID',
      label: currentProjectLabel.singular,
      required: true
    }
  };

  const isMaidenNameRequired =
    currentProjectType !== KnownProjectDtoProjectType.Retailer &&
    currentProjectType !== KnownProjectDtoProjectType.Store;

  const formConfig = {
    ...formConfigBase,
    ...(isMaidenNameRequired && {
      maidenName: {
        name: 'maidenName',
        label: 'Maiden name',
        required: false
      } as FormConfigItem<'maidenName'>
    })
  };

  if (
    currentProjectType === KnownProjectDtoProjectType.Retailer ||
    currentProjectType === KnownProjectDtoProjectType.Store
  ) {
    formConfig.maidenName = undefined;
  }
  return {
    formConfig,
    mobileCountry,
    handleSubmit,
    control,
    setValue,
    watch
  };
}
